import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Dialog, DialogContent, IconButton } from '@mui/material';
import { useEffect, useState } from 'react';

import { convertHeicBlobToJpegUrl } from '../../common/services/heic.service';
import { CommonHelperService } from '../../common/services/helper.service';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { previewImageSelector, removePreviewImage } from '../../redux/slices/imagePreview.slice';
import { LoaderVertical } from './Loader';

const PreviewImage = () => {
  const dispatch = useAppDispatch();
  const { imageUrls, index } = useAppSelector(previewImageSelector);
  const [localIndex, setLocalIndex] = useState<number | null>(null);
  const [heicPreview, setHeicPreview] = useState<{ sourceUrl: string; objectUrl: string }>();
  const [heicLoading, setHeicLoading] = useState(false);

  useEffect(() => {
    setLocalIndex(null);
  }, [imageUrls, index]);

  const activeIndex = localIndex ?? index ?? 0;
  const currentImage = imageUrls[activeIndex];
  const imageUrl = currentImage?.url;
  const isHeic = currentImage ? CommonHelperService.isHeicFile(currentImage) : false;
  const convertedHeicUrl = heicPreview && heicPreview.sourceUrl === imageUrl ? heicPreview.objectUrl : undefined;

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | undefined;

    if (!isHeic || !imageUrl) {
      setHeicPreview(undefined);
      setHeicLoading(false);
      return undefined;
    }

    setHeicLoading(true);

    const convertHeicToJpeg = async () => {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      return convertHeicBlobToJpegUrl(blob);
    };

    convertHeicToJpeg()
      .then((jpegUrl) => {
        if (cancelled) {
          URL.revokeObjectURL(jpegUrl);
          return;
        }
        objectUrl = jpegUrl;
        setHeicPreview({ sourceUrl: imageUrl, objectUrl: jpegUrl });
      })
      .catch(() => {
        if (!cancelled) {
          setHeicPreview(undefined);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setHeicLoading(false);
        }
      });

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [imageUrl, isHeic]);

  const closePreview = () => {
    dispatch(removePreviewImage());
  };
  const showPrev = () => {
    setLocalIndex(activeIndex - 1);
  };
  const showNext = () => {
    setLocalIndex(activeIndex + 1);
  };

  const totalImage = imageUrls.length;

  return (
    <Dialog
      maxWidth={false}
      PaperProps={{
        sx: {
          backgroundColor: 'transparent',
          borderRadius: '10px',
          position: 'static',
        },
      }}
      open={imageUrls.length > 0}
      onClose={closePreview}
    >
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ display: 'flex', backgroundColor: '#fff' }}>
          {isHeic ? (
            heicLoading || !convertedHeicUrl ? (
              <LoaderVertical />
            ) : (
              <img crossOrigin="anonymous" style={{ maxWidth: '100%', maxHeight: '100%' }} src={convertedHeicUrl} />
            )
          ) : imageUrl ? (
            <img crossOrigin="anonymous" style={{ maxWidth: '100%', maxHeight: '100%' }} src={imageUrl} />
          ) : (
            <LoaderVertical />
          )}
        </Box>
        <Box sx={{ position: 'absolute', right: 10, top: 10 }}>
          <IconButton onClick={() => closePreview()} sx={{ background: '#c2d1d9', '&:hover': { background: '#c2d1d9' } }}>
            <CloseIcon />
          </IconButton>
        </Box>
        {activeIndex > 0 && (
          <Box sx={{ position: 'absolute', left: 10, top: '50%' }}>
            <IconButton onClick={() => showPrev()} sx={{ background: '#c2d1d9', '&:hover': { background: '#c2d1d9' } }}>
              <ArrowBackIosIcon />
            </IconButton>
          </Box>
        )}
        {totalImage > activeIndex + 1 && (
          <Box sx={{ position: 'absolute', right: 10, top: '50%' }}>
            <IconButton onClick={() => showNext()} sx={{ background: '#c2d1d9', '&:hover': { background: '#c2d1d9' } }}>
              <ArrowForwardIosIcon />
            </IconButton>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PreviewImage;
